import { useEffect, useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  Star,
  MapPin,
  Heart,
  Share2,
  Phone,
  Navigation,
  Clock,
  Users,
  Gift,
  ChevronLeft,
  ExternalLink,
  Calendar,
  Utensils,
  Camera,
  X,
  ThumbsUp,
  MessageCircle
} from 'lucide-react';
import type { Restaurant, MenuItem, UserType } from '../../App';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { getMenuItems } from '../../lib/mockData';
import { BookTableModal } from './BookTableModal';
import { RestaurantMenuModal } from './RestaurantMenuModal';
import { api } from '../../lib/api';

interface RestaurantDetailsViewProps {
  restaurant: Restaurant;
  onBack: () => void;
  user: UserType;
}

type TabType = 'overview' | 'menu' | 'photos' | 'reviews' | 'offers' | 'about';

interface Review {
  id: string;
  userName: string;
  userAvatar: string;
  rating: number;
  comment: string;
  date: string;
  foodRating: number;
  serviceRating: number;
  ambianceRating: number;
  helpful: number;
}

interface Offer {
  id: string;
  // Coupon code to apply at checkout (comes from backend `coupons.code`)
  code?: string;
  title: string;
  description: string;
  validity: string;
  type: 'discount' | 'deal' | 'happy-hour';
}

interface MasterAmenity {
  id?: string;
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

interface RestaurantSeatingPreference {
  id: string;
  seatingPreferenceId: string;
  isAvailable: boolean;
  seatingPreference: {
    id: string;
    name: string;
    code: string;
    isActive: boolean;
  };
}

export function RestaurantDetailsView({ restaurant, onBack, user }: RestaurantDetailsViewProps) {
  const { formatMoney } = useLocale();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isSaved, setIsSaved] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [amenities, setAmenities] = useState<string[]>([]);
  const [seatingPreferences, setSeatingPreferences] = useState<RestaurantSeatingPreference[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offerClaimMessage, setOfferClaimMessage] = useState<string | null>(null);

  const menuItems = getMenuItems(restaurant.id);

  // Mock data
  const highlights = ['Romantic Dining', 'Family Friendly', 'Live Music', 'Rooftop Seating'];

  useEffect(() => {
    let isMounted = true;

    const loadAmenities = async () => {
      try {
        const response = await api.masterData.amenities.getAll();
        const masterAmenities: MasterAmenity[] = Array.isArray(response?.data) ? response.data : [];
        const allAmenities = masterAmenities
          .filter(item => item.isActive !== false)
          .map(item => String(item.name || item.description || item.code || '').trim())
          .filter(Boolean);

        if (!isMounted) return;

        setAmenities(allAmenities);
      } catch {
        if (isMounted) {
          const fallbackAmenities = (restaurant.amenities || []).map(value =>
            value
              .split(/[-_]/g)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          );
          setAmenities(fallbackAmenities);
        }
      }
    };

    loadAmenities();

    return () => {
      isMounted = false;
    };
  }, [restaurant.amenities]);

  useEffect(() => {
    let isMounted = true;

    const loadSeatingPreferences = async () => {
      try {
        const response = await api.seatingPreferences.getByRestaurant(restaurant.id);
        if (!isMounted) return;
        setSeatingPreferences(Array.isArray(response?.data) ? response.data : []);
      } catch {
        if (isMounted) {
          setSeatingPreferences([]);
        }
      }
    };

    void loadSeatingPreferences();

    return () => {
      isMounted = false;
    };
  }, [restaurant.id]);

  useEffect(() => {
    let isMounted = true;

    const loadReviews = async () => {
      setReviewsLoading(true);
      try {
        const response = await api.reviews.getByRestaurant(restaurant.id);
        if (!isMounted) return;

        const rawItems = Array.isArray(response?.data) ? response.data : [];
        setReviews(
          rawItems.map((review: any) => {
            const userName = String(review?.customer_name || review?.customerName || review?.userName || 'Customer').trim();
            const userAvatar =
              String(review?.customer_avatar || review?.customerAvatar || '').trim() ||
              `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(userName || 'Customer')}`;
            const rating = Math.max(0, Math.min(5, Number(review?.rating ?? 0) || 0));
            const createdAt = review?.created_at ?? review?.createdAt ?? review?.date;

            return {
              id: String(review?.id || `${userName}-${createdAt || Date.now()}`),
              userName: userName || 'Customer',
              userAvatar,
              rating,
              comment: String(review?.comment || 'No written review provided.').trim(),
              date: createdAt
                ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                : 'Recently',
              foodRating: rating,
              serviceRating: rating,
              ambianceRating: rating,
              helpful: Number(review?.helpful ?? 0) || 0,
            };
          })
        );
      } catch {
        if (isMounted) setReviews([]);
      } finally {
        if (isMounted) setReviewsLoading(false);
      }
    };

    void loadReviews();

    return () => {
      isMounted = false;
    };
  }, [restaurant.id]);

  const photos = [
    restaurant.coverImage,
    restaurant.logo,
    ...menuItems.slice(0, 12).map(item => item.image).filter(Boolean)
  ];

  const fallbackOffers: Offer[] = [
    {
      id: 'fallback-1',
      title: 'Special Offer',
      description: 'Limited time deal.',
      validity: 'Limited time offer',
      type: 'deal',
    },
  ];

  const toDateLabel = (value: unknown): string | null => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const mapCouponToOffer = (coupon: any): Offer => {
    const title = String(coupon?.name || coupon?.title || coupon?.code || 'Offer').trim();
    const code = String(coupon?.code || '').trim().toUpperCase() || undefined;
    const description = String(coupon?.description || coupon?.details || '').trim() || 'Special offer';

    const validUntil = toDateLabel(coupon?.valid_until ?? coupon?.validUntil);
    const validFrom = toDateLabel(coupon?.valid_from ?? coupon?.validFrom);

    const validity = validUntil
      ? `Valid till ${validUntil}`
      : validFrom
        ? `Valid from ${validFrom}`
        : 'Limited time offer';

    const discountTypeCode = String(coupon?.discount_type_code || coupon?.discountTypeCode || '').trim().toUpperCase();
    const inferredType: Offer['type'] =
      /HAPPY/.test(title.toUpperCase()) ? 'happy-hour' : discountTypeCode === 'PERCENTAGE' ? 'discount' : 'deal';

    return {
      id: String(coupon?.id || coupon?.coupon_id || title),
      code,
      title,
      description,
      validity,
      type: inferredType,
    };
  };

  useEffect(() => {
    let isMounted = true;

    const loadOffers = async () => {
      setOffersLoading(true);
      try {
        const response = await api.coupons.getByRestaurant(restaurant.id);
        const data = Array.isArray(response?.data) ? response.data : [];
        const mapped = data.map(mapCouponToOffer);

        if (!isMounted) return;
        setOffers(mapped);
      } catch {
        if (isMounted) setOffers(fallbackOffers);
      } finally {
        if (isMounted) setOffersLoading(false);
      }
    };

    void loadOffers();

    return () => {
      isMounted = false;
    };
  }, [restaurant.id]);

  useEffect(() => {
    if (!offerClaimMessage) return;
    const timer = setTimeout(() => setOfferClaimMessage(null), 2500);
    return () => clearTimeout(timer);
  }, [offerClaimMessage]);

  const handleClaimOffer = async (offer: Offer) => {
    // In this app, "offers" are backed by coupons. Claiming means: copy code + prefill at checkout.
    const rawCode = offer.code || offer.title;
    const normalizedCode = String(rawCode || '').trim().toUpperCase();

    if (!normalizedCode) {
      setOfferClaimMessage('Coupon code not available for this offer.');
      return;
    }

    try {
      localStorage.setItem('pending_coupon_code', normalizedCode);
      localStorage.setItem('pending_coupon_restaurant_id', String(restaurant.id));
    } catch {
      // Ignore storage failures (private mode / disabled storage)
    }

    try {
      await navigator.clipboard.writeText(normalizedCode);
      setOfferClaimMessage(`Coupon ${normalizedCode} copied. Apply it at checkout.`);
    } catch {
      // Clipboard may be blocked by the browser; still show the code.
      setOfferClaimMessage(`Use coupon code: ${normalizedCode}`);
    }
  };

  const reviewAverage =
    reviews.length > 0 ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviews.length : 0;
  const avgRating = reviewAverage > 0 ? Number(reviewAverage.toFixed(1)) : restaurant.rating;
  const totalReviews = reviews.length > 0 ? reviews.length : restaurant.totalRatings;
  const avgFoodRating = avgRating;
  const avgServiceRating = avgRating;
  const avgAmbianceRating = avgRating;

  const extractTimeValue = (value: string | null | undefined): string => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const matched = raw.match(/(\d{2}):(\d{2})(?::\d{2})?/);
    return matched ? `${matched[1]}:${matched[2]}` : raw;
  };

  const formatTimeLabel = (value: string | null | undefined): string => {
    const normalized = extractTimeValue(value);
    if (!normalized) return '--:--';
    const [hoursRaw, minutesRaw] = normalized.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return normalized;
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const menuCategories = [
    { name: 'Starters', items: menuItems.filter(item => item.categoryId === 'cat-1').slice(0, 3) },
    { name: 'Main Course', items: menuItems.filter(item => item.categoryId === 'cat-2').slice(0, 3) },
    { name: 'Desserts', items: menuItems.filter(item => item.categoryId === 'cat-3').slice(0, 3) },
    { name: 'Beverages', items: menuItems.slice(0, 3) }
  ];

  const openingHours = {
    days: 'All Days',
    slots: [`${formatTimeLabel(restaurant.openTime)} - ${formatTimeLabel(restaurant.closeTime)}`]
  };

  const isOpen = restaurant.isOpen ?? true;
  const closeTime = formatTimeLabel(restaurant.closeTime);

  const handleCall = () => {
    window.location.href = `tel:${restaurant.phone}`;
  };

  const handleDirections = () => {
    window.open(`https://maps.google.com/?q=${encodeURIComponent(restaurant.address)}`, '_blank');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: restaurant.name,
        text: `Check out ${restaurant.name} on our app!`,
        url: window.location.href
      });
    } else {
      alert('Share link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Cover Image */}
      <div className="relative h-[400px] md:h-[500px]">
        <ImageWithFallback
          src={restaurant.coverImage || restaurant.logo}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10"
        >
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>

        {/* Floating Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button
            onClick={() => setIsSaved(!isSaved)}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : 'text-gray-900'}`} />
          </button>
          <button
            onClick={handleShare}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
          >
            <Share2 className="w-5 h-5 text-gray-900" />
          </button>
        </div>

        {/* Restaurant Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">{restaurant.name}</h1>
            <p className="text-lg text-white/90 mb-3">{restaurant.description}</p>
            
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2 bg-[#4ADE80] px-3 py-1.5 rounded-lg">
                <Star className="w-4 h-4 fill-white" />
                <span className="font-bold">{avgRating}</span>
                <span className="text-sm opacity-90">({totalReviews} reviews)</span>
              </div>
              <span className="text-white/90">• {formatMoney(1200)} for two</span>
              <div className="flex items-center gap-1.5 text-white/90">
                <MapPin className="w-4 h-4" />
                <span>{restaurant.address.split(',')[0]} • 2.5 km</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isOpen ? (
                <span className="text-[#4ADE80] font-medium flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-[#4ADE80] rounded-full"></div>
                  Online • Closes at {closeTime}
                </span>
              ) : (
                <span className="text-red-400 font-medium flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Offline
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Primary Action Buttons */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={() => setShowBookingModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#60A5FA] text-white rounded-lg hover:bg-[#3B82F6] transition-colors font-medium shadow-md"
            >
              <Calendar className="w-5 h-5" />
              <span>Book Table</span>
            </button>
            <button
              onClick={handleCall}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#4ADE80] text-white rounded-lg hover:bg-[#22C55E] transition-colors font-medium shadow-md"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </button>
            <button
              onClick={handleDirections}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <Navigation className="w-5 h-5" />
              <span>Directions</span>
            </button>
            <button
              onClick={() => setShowMenuModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <Utensils className="w-5 h-5" />
              <span>View Menu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[81px] z-30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-6 no-scrollbar">
            {(['overview', 'menu', 'photos', 'reviews', 'offers', 'about'] as TabType[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#60A5FA] text-[#60A5FA]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About {restaurant.name}</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {restaurant.name} is a premium dining destination offering an exquisite blend of flavors and ambiance. 
                Our carefully curated menu features authentic dishes prepared by our expert chefs using the finest ingredients. 
                Whether you're planning a romantic dinner, family celebration, or business lunch, we provide the perfect setting 
                with our elegant interiors and attentive service.
              </p>
            </div>

            {/* Highlights */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">What Makes Us Special</h3>
              <div className="flex flex-wrap gap-3">
                {highlights.map((highlight, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full text-[#60A5FA] font-medium dark:from-slate-800 dark:to-slate-800 dark:border-blue-400/40 dark:text-blue-300"
                  >
                    {highlight}
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Amenities & Facilities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {amenities.map((amenity, index) => {
                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <span className="text-sm text-gray-700 text-center">{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Available Seating</h3>
              <div className="flex flex-wrap gap-3">
                {seatingPreferences.length > 0 ? (
                  seatingPreferences.map((item) => (
                    <div
                      key={item.id}
                      className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 rounded-full text-orange-700 font-medium"
                    >
                      {item.seatingPreference.name}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-gray-500">
                    Seating preferences will appear here when configured
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 dark:from-slate-800 dark:to-slate-700 dark:border-blue-400/30">
                <div className="flex items-center gap-3 mb-2">
                  <Star className="w-8 h-8 text-[#60A5FA]" />
                  <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">{avgRating}</span>
                </div>
                <p className="text-gray-700 dark:text-slate-200">Dining Rating</p>
                <p className="text-sm text-gray-600 dark:text-slate-400">{totalReviews} reviews</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 dark:from-slate-800 dark:to-slate-700 dark:border-green-400/30">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-8 h-8 text-[#4ADE80]" />
                  <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">45 min</span>
                </div>
                <p className="text-gray-700 dark:text-slate-200">Avg Wait Time</p>
                <p className="text-sm text-gray-600 dark:text-slate-400">For walk-ins</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200 dark:from-slate-800 dark:to-slate-700 dark:border-orange-400/30">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-8 h-8 text-[#FB923C]" />
                  <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">250+</span>
                </div>
                <p className="text-gray-700 dark:text-slate-200">Seating Capacity</p>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {seatingPreferences.length > 0
                    ? seatingPreferences.map((item) => item.seatingPreference.name).join(' • ')
                    : 'Configured by restaurant'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Menu Tab */}
        {activeTab === 'menu' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Menu Preview</h2>
              <button
                onClick={() => setShowMenuModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#60A5FA] text-white rounded-lg hover:bg-[#3B82F6] transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View Full Menu
              </button>
            </div>

            {menuCategories.map((category, idx) => (
              <div key={idx}>
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#60A5FA]">
                  {category.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.items.map(item => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow">
                      {item.image && (
                        <ImageWithFallback
                          src={item.image}
                          alt={item.name}
                          className="w-full h-40 object-cover rounded-lg mb-3"
                        />
                      )}
                      <div className="flex items-start gap-2 mb-1">
                        {/* Veg/Non-Veg Indicator */}
                        <div className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          item.tags?.includes('veg') 
                            ? 'border-green-600' 
                            : 'border-red-600'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            item.tags?.includes('veg') 
                              ? 'bg-green-600' 
                              : 'bg-red-600'
                          }`}></div>
                        </div>
                        <h4 className="font-semibold text-gray-900 flex-1">{item.name}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                      <p className="text-[#60A5FA] font-bold">{formatMoney(item.price)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Photos ({photos.length})</h2>
              <div className="flex gap-2">
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  All
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Food
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Ambiance
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.slice(0, showAllPhotos ? photos.length : 12).map((photo, idx) => (
                <div
                  key={idx}
                  onClick={() => setSelectedPhoto(photo || '')}
                  className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
                >
                  <ImageWithFallback
                    src={photo || ''}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>

            {!showAllPhotos && photos.length > 12 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAllPhotos(true)}
                  className="px-6 py-3 bg-[#60A5FA] text-white rounded-lg hover:bg-[#3B82F6] transition-colors font-medium"
                >
                  View All {photos.length} Photos
                </button>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-8">
            {/* Rating Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200 dark:from-slate-800 dark:to-slate-700 dark:border-blue-400/30">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-6xl font-bold text-gray-900 mb-2 dark:text-slate-100">{avgRating}</div>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-[#FB923C] text-[#FB923C]" />
                    ))}
                  </div>
                  <p className="text-gray-700 font-medium dark:text-slate-200">{totalReviews} Dining Reviews</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-700 font-medium dark:text-slate-200">Food</span>
                      <span className="text-gray-900 font-bold dark:text-slate-100">{avgFoodRating}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden dark:bg-slate-600">
                      <div className="h-full bg-[#4ADE80]" style={{ width: `${(avgFoodRating / 5) * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-700 font-medium dark:text-slate-200">Service</span>
                      <span className="text-gray-900 font-bold dark:text-slate-100">{avgServiceRating}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden dark:bg-slate-600">
                      <div className="h-full bg-[#60A5FA]" style={{ width: `${(avgServiceRating / 5) * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-700 font-medium dark:text-slate-200">Ambiance</span>
                      <span className="text-gray-900 font-bold dark:text-slate-100">{avgAmbianceRating}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden dark:bg-slate-600">
                      <div className="h-full bg-[#FB923C]" style={{ width: `${(avgAmbianceRating / 5) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual Reviews */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900">Customer Reviews</h3>
              {reviewsLoading && (
                <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-500">
                  Loading customer reviews...
                </div>
              )}
              {!reviewsLoading && reviews.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-500">
                  No customer reviews yet.
                </div>
              )}
              {reviews.map(review => (
                <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <img
                      src={review.userAvatar}
                      alt={review.userName}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                          <p className="text-sm text-gray-500">{review.date}</p>
                        </div>
                        <div className="flex items-center gap-1 bg-[#4ADE80] text-white px-3 py-1 rounded-lg">
                          <Star className="w-4 h-4 fill-white" />
                          <span className="font-bold">{review.rating}</span>
                        </div>
                      </div>

                      {/* Detailed Ratings */}
                      <div className="flex gap-4 mb-3">
                        <span className="text-xs text-gray-600">Food: {review.foodRating}★</span>
                        <span className="text-xs text-gray-600">Service: {review.serviceRating}★</span>
                        <span className="text-xs text-gray-600">Ambiance: {review.ambianceRating}★</span>
                      </div>

                      <p className="text-gray-700 mb-4 leading-relaxed">{review.comment}</p>

                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-2 text-gray-600 hover:text-[#60A5FA] transition-colors">
                          <ThumbsUp className="w-4 h-4" />
                          <span className="text-sm">Helpful ({review.helpful})</span>
                        </button>
                        <button className="flex items-center gap-2 text-gray-600 hover:text-[#60A5FA] transition-colors">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-sm">Reply</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Current Offers & Deals</h2>
              <Gift className="w-8 h-8 text-[#FB923C]" />
            </div>

            {offerClaimMessage && (
              <div className="bg-white border-2 border-orange-200 rounded-2xl p-4 text-gray-800">
                {offerClaimMessage}
              </div>
            )}

            {offersLoading && (
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 text-gray-700">
                Loading offers...
              </div>
            )}

            {!offersLoading && offers.length === 0 && (
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 text-gray-700">
                No active offers available right now.
              </div>
            )}

            {!offersLoading && offers.map(offer => (
              <div
                key={offer.id}
                className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-[#FB923C] rounded-2xl p-6 relative overflow-hidden dark:from-slate-800 dark:to-slate-800 dark:border-orange-400/60"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#FB923C]/10 rounded-full -mr-12 -mt-12 dark:bg-[#FB923C]/15"></div>
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#FB923C] rounded-xl flex items-center justify-center">
                        <Gift className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">{offer.title}</h3>
                        <p className="text-sm text-[#FB923C] font-medium">{offer.type === 'happy-hour' ? '🎉 Happy Hours' : '🎁 Special Offer'}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3 leading-relaxed dark:text-slate-200">{offer.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center gap-1 dark:text-slate-400">
                      <Clock className="w-4 h-4" />
                      {offer.validity}
                    </span>
                    <button
                      onClick={() => handleClaimOffer(offer)}
                      className="px-4 py-2 bg-[#FB923C] text-white rounded-lg hover:bg-[#F97316] transition-colors font-medium"
                    >
                      Claim Offer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="space-y-8">
            {/* Location & Contact */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Location & Contact</h2>
              
              {/* Map Placeholder */}
              <div className="bg-gray-200 rounded-2xl h-64 mb-6 flex items-center justify-center overflow-hidden">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Interactive Map</p>
                  <p className="text-sm text-gray-500">{restaurant.address}</p>
                </div>
              </div>

              {/* Contact Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#4ADE80]/10 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#4ADE80]" />
                    </div>
                    <h3 className="font-bold text-gray-900">Phone</h3>
                  </div>
                  <p className="text-gray-700 mb-3">{restaurant.phone}</p>
                  <button
                    onClick={handleCall}
                    className="w-full px-4 py-2 bg-[#4ADE80] text-white rounded-lg hover:bg-[#22C55E] transition-colors font-medium"
                  >
                    Call Now
                  </button>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#60A5FA]/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#60A5FA]" />
                    </div>
                    <h3 className="font-bold text-gray-900">Address</h3>
                  </div>
                  <p className="text-gray-700 mb-3">{restaurant.address}</p>
                  <button
                    onClick={handleDirections}
                    className="w-full px-4 py-2 bg-[#60A5FA] text-white rounded-lg hover:bg-[#3B82F6] transition-colors font-medium"
                  >
                    Get Directions
                  </button>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Opening Hours</h3>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-gray-700 font-medium">{openingHours.days}</span>
                  <div className="text-right">
                    {openingHours.slots.map((slot) => (
                      <p key={slot} className="text-gray-900 font-bold">
                        {slot}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <ImageWithFallback
            src={selectedPhoto}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 p-4 md:hidden z-40">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleCall}
            className="flex flex-col items-center gap-1 px-4 py-2 bg-[#4ADE80] text-white rounded-lg hover:bg-[#22C55E] transition-colors"
          >
            <Phone className="w-5 h-5" />
            <span className="text-xs font-medium">Call</span>
          </button>
          <button
            onClick={handleDirections}
            className="flex flex-col items-center gap-1 px-4 py-2 bg-[#60A5FA] text-white rounded-lg hover:bg-[#3B82F6] transition-colors"
          >
            <Navigation className="w-5 h-5" />
            <span className="text-xs font-medium">Directions</span>
          </button>
          <button
            onClick={() => setShowBookingModal(true)}
            className="flex flex-col items-center gap-1 px-4 py-2 bg-[#FB923C] text-white rounded-lg hover:bg-[#F97316] transition-colors"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-medium">Book</span>
          </button>
        </div>
      </div>

      {/* Book Table Modal */}
      {showBookingModal && (
        <BookTableModal
          restaurant={restaurant}
          seatingPreferences={seatingPreferences}
          onClose={() => setShowBookingModal(false)}
        />
      )}

      {showMenuModal && (
        <RestaurantMenuModal
          restaurant={restaurant}
          onClose={() => setShowMenuModal(false)}
        />
      )}
    </div>
  );
}
